import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import AcademyShowcase from '../components/landing/AcademyShowcase';
import PricingComparison from '../components/landing/PricingComparison';


const CreatorProfiles = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();


  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/me');
    } else {
      navigate(`/auth?mode=signup&redirect=${encodeURIComponent('/onboarding')}`);
    }
  };

  const handleCreatorCheckout = (tier = 'standard') => {
    const checkoutTier = tier === 'pro' ? 'grandmaster' : 'master';
    if (!currentUser) {
      navigate(`/auth?mode=signup&redirect=${encodeURIComponent(`/onboarding?checkout=${checkoutTier}`)}`);
      return;
    }
    navigate(`/onboarding?checkout=${checkoutTier}`);
  };

  const handleLogin = () => {
    navigate('/auth?mode=login&context=creator');
  };

  // Capture referral code
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('referralCode', JSON.stringify({
        ref,
        timestamp: Date.now(),
        expiresAt: Date.now() + (90 * 24 * 60 * 60 * 1000)
      }));
      sessionStorage.setItem('referralCode', ref);
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Horizontal scroll integration items
  const integrationItems = useMemo(() => [
    { key: 'chessweb', type: 'image', src: '/Images/ChessWeb.site.png', alt: 'ChessWeb.site', bg: '#2A2A2A' },
    { key: 'for-chess', type: 'tag', label: 'For Chess', bg: '#E8DCC4', textColor: '#1F1F1F' },
    { key: 'lichess', type: 'image', src: '/Images/LiChess.png', alt: 'Lichess', bg: '#2B2421' },

    { key: 'for-creators', type: 'tag', label: 'For Creators', bg: '#FB923C', textColor: '#1F1F1F' },
    { key: 'x', type: 'icon', iconClass: 'fab fa-x-twitter', label: 'X (Twitter)', bg: '#000000', iconColor: '#FFFFFF' },

    { key: 'for-coaches', type: 'tag', label: 'For Coaches', bg: '#34D399', textColor: '#1F1F1F' },
    { key: 'fide', type: 'image', src: '/Images/FIDE.png', alt: 'FIDE', bg: '#1E3A5F' },
    { key: 'for-streamers', type: 'tag', label: 'For Streamers', bg: '#A78BFA', textColor: '#1F1F1F' },
    { key: 'twitch', type: 'icon', iconClass: 'fab fa-twitch', label: 'Twitch', bg: '#9146FF' },
    { key: 'for-builders', type: 'tag', label: 'For Builders', bg: '#4ADE80', textColor: '#1F1F1F' },
    { key: 'chesscom', type: 'image', src: '/Images/Chesscom.png', alt: 'Chess.com', bg: '#2C3E23' },

    { key: 'for-marketers', type: 'tag', label: 'For Marketers', bg: '#60A5FA', textColor: '#1F1F1F' },
    { key: 'tiktok', type: 'icon', iconClass: 'fab fa-tiktok', label: 'TikTok', bg: '#000000', iconColor: '#FFFFFF' },
    { key: 'for-youtubers', type: 'tag', label: 'For YouTubers', bg: '#F87171', textColor: '#1F1F1F' },
    { key: 'youtube', type: 'icon', iconClass: 'fab fa-youtube', label: 'YouTube', bg: '#FF0000' },

    { key: 'for-players', type: 'tag', label: 'For Players', bg: '#60A5FA', textColor: '#1F1F1F' },
    { key: 'instagram', type: 'icon', iconClass: 'fab fa-instagram', label: 'Instagram', gradient: 'linear-gradient(135deg, #F09433 0%, #E6683C 25%, #DC2743 50%, #CC2366 75%, #BC1888 100%)' },
    { key: 'indiechess', type: 'tag', label: 'By Indie Chess', href: 'https://indie.chessweb.site', bg: '#FFD700', textColor: '#1a1a1a' },


    { key: 'discord', type: 'icon', iconClass: 'fab fa-discord', label: 'Discord', bg: '#5865F2' }
  ], []);

  const loopedItems = useMemo(() => {
    const originals = integrationItems.map((item, index) => ({
      ...item,
      loopKey: `${item.key}-original-${index}`
    }));
    const duplicates = integrationItems.map((item, index) => ({
      ...item,
      loopKey: `${item.key}-duplicate-${index}`
    }));
    return [...originals, ...duplicates];
  }, [integrationItems]);

  const scrollContainerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const heroRef = useRef(null);
  const phoneRef = useRef(null);
  const laptopRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const accumulatedScrollRef = useRef(0);
  const touchStartYRef = useRef(0);
  const lastTouchYRef = useRef(0);

  // Mobile detection
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll hijacking effect
  useEffect(() => {
    const heroElement = heroRef.current;
    const phoneElement = phoneRef.current;
    if (!heroElement || !phoneElement) return;

    const maxScroll = window.innerHeight * 1.2; // Amount of scroll to fully reveal image

    // Helper function to check if phone is in "scroll hijack zone"
    const isPhoneInHijackZone = () => {
      const phoneRect = phoneElement.getBoundingClientRect();
      const phoneCenter = phoneRect.top + (phoneRect.height / 2);

      // Phone is in zone when it's roughly centered in viewport
      // Trigger zone: when phone center is between 30% and 70% of viewport height
      const minTrigger = window.innerHeight * 0.3;
      const maxTrigger = window.innerHeight * 0.7;

      return phoneCenter >= minTrigger && phoneCenter <= maxTrigger;
    };

    // Helper function to lock body scroll
    const lockBodyScroll = () => {
      if (!isScrollLocked) {
        const scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        setIsScrollLocked(true);
      }
    };

    // Helper function to unlock body scroll
    const unlockBodyScroll = () => {
      if (isScrollLocked) {
        const scrollY = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';

        // Restore scroll position without animation
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        setIsScrollLocked(false);
      }
    };

    const handleWheel = (e) => {
      const inZone = isPhoneInHijackZone();

      // Direction-aware capture logic:
      // - Scrolling DOWN: Capture if in zone OR already scrolled (to continue animation)
      // - Scrolling UP: Capture ONLY if in zone AND already scrolled (don't hijack when off-screen)
      let shouldCapture = false;
      if (e.deltaY > 0) {
        // Scrolling down
        shouldCapture = inZone || accumulatedScrollRef.current > 0;
      } else {
        // Scrolling up
        shouldCapture = inZone && accumulatedScrollRef.current > 0;
      }

      if (shouldCapture) {
        const newAccumulated = accumulatedScrollRef.current + e.deltaY;

        // Scrolling down and image not fully scrolled
        if (e.deltaY > 0 && accumulatedScrollRef.current < maxScroll) {
          e.preventDefault();
          accumulatedScrollRef.current = Math.min(newAccumulated, maxScroll);
          setScrollProgress(accumulatedScrollRef.current / maxScroll);
        }
        // Scrolling up and image not at start
        else if (e.deltaY < 0 && accumulatedScrollRef.current > 0) {
          e.preventDefault();
          accumulatedScrollRef.current = Math.max(newAccumulated, 0);
          setScrollProgress(accumulatedScrollRef.current / maxScroll);
        }
        // At boundaries - don't preventDefault, allow normal scroll
      }
    };

    // Reset when scrolling back to top of page
    const handleScroll = () => {
      if (window.scrollY === 0) {
        accumulatedScrollRef.current = 0;
        setScrollProgress(0);
      }
      setShowBackToTop(window.scrollY > 400);
    };

    // Touch event handlers for mobile
    const handleTouchStart = (e) => {
      if (!isMobile) return;
      const touch = e.touches[0];
      touchStartYRef.current = touch.clientY;
      lastTouchYRef.current = touch.clientY;
    };

    const handleTouchMove = (e) => {
      if (!isMobile) return;

      const touch = e.touches[0];
      const inZone = isPhoneInHijackZone();

      // Calculate delta (negative = scrolling down, positive = scrolling up)
      const deltaY = lastTouchYRef.current - touch.clientY;
      lastTouchYRef.current = touch.clientY;

      // Direction-aware capture logic (same as desktop):
      // - Scrolling DOWN: Capture if in zone OR already scrolled OR locked
      // - Scrolling UP: Capture ONLY if in zone AND (already scrolled OR locked)
      let shouldCapture = false;
      if (deltaY > 0) {
        // Scrolling down (dragging up)
        shouldCapture = inZone || accumulatedScrollRef.current > 0 || isScrollLocked;
      } else {
        // Scrolling up (dragging down)
        shouldCapture = inZone && (accumulatedScrollRef.current > 0 || isScrollLocked);
      }

      if (shouldCapture) {
        const newAccumulated = accumulatedScrollRef.current + (deltaY * 2); // Multiply for more sensitivity on mobile

        // Scrolling down (dragging up) and image not fully scrolled
        if (deltaY > 0 && accumulatedScrollRef.current < maxScroll) {
          e.preventDefault();
          e.stopPropagation();
          lockBodyScroll();
          accumulatedScrollRef.current = Math.min(newAccumulated, maxScroll);
          setScrollProgress(accumulatedScrollRef.current / maxScroll);

          // Release lock when fully scrolled
          if (accumulatedScrollRef.current >= maxScroll) {
            unlockBodyScroll();
          }
        }
        // Scrolling up (dragging down) and image not at start
        else if (deltaY < 0 && accumulatedScrollRef.current > 0) {
          e.preventDefault();
          e.stopPropagation();
          lockBodyScroll();
          accumulatedScrollRef.current = Math.max(newAccumulated, 0);
          setScrollProgress(accumulatedScrollRef.current / maxScroll);

          // Release lock when back at top
          if (accumulatedScrollRef.current <= 0) {
            unlockBodyScroll();
          }
        }
        // At boundaries - unlock and allow normal scroll to continue
        else if (deltaY > 0 && accumulatedScrollRef.current >= maxScroll) {
          unlockBodyScroll();
          // Don't preventDefault - let scroll continue naturally
        }
        else if (deltaY < 0 && accumulatedScrollRef.current <= 0) {
          unlockBodyScroll();
          // Don't preventDefault - let scroll continue naturally
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isMobile) return;
      // Reset touch tracking
      touchStartYRef.current = 0;
      lastTouchYRef.current = 0;
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScroll);

    // Add touch event listeners (always, handlers check isMobile internally)
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);

      // Clean up body scroll lock on unmount
      if (isScrollLocked) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
      }
    };
  }, [isScrollLocked, isMobile]);

  // Independent scroll-to-top button visibility (separate from scroll-jacking)
  useEffect(() => {
    const checkScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', checkScroll, { passive: true });
    return () => window.removeEventListener('scroll', checkScroll);
  }, []);


  // Horizontal scroll auto-animation
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    let lastTimestamp = performance.now();
    const scrollSpeed = 0.4; // pixels per frame at 60fps

    const step = (timestamp) => {
      if (!container) {
        return;
      }

      const delta = (timestamp - lastTimestamp) || 16;
      const velocity = scrollSpeed * (delta / (1000 / 120));

      container.scrollLeft += velocity;
      const loopWidth = container.scrollWidth / 2;
      if (loopWidth > 0 && container.scrollLeft >= loopWidth) {
        container.scrollLeft -= loopWidth;
      }

      lastTimestamp = timestamp || performance.now();
      animationFrameRef.current = requestAnimationFrame(step);
    };

    // Initialize and start
    container.scrollLeft = 0;
    lastTimestamp = performance.now();
    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [loopedItems]);

  return (
    <>
      <Helmet>
        <title>ChessWeb.site | Custom Websites for Chess Coaches, Players & Organizers</title>
        <meta name="description" content="Build your chess website in minutes. Coaching academies, online shops, tournament registration, blogs, and more — the all-in-one platform for chess professionals." />
        <link rel="canonical" href="https://chessweb.site/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chessweb.site/" />
        <meta property="og:title" content="ChessWeb.site | Custom Websites for Chess Coaches, Players & Organizers" />
        <meta property="og:description" content="Launch your website, build your brand, and grow your revenue. The all-in-one platform for Coaches, Clubs, Creators, and Players." />
        <meta property="og:image" content="https://chessweb.site/SocialSharing.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://chessweb.site/" />
        <meta name="twitter:title" content="ChessWeb.site | Custom Websites for Chess Coaches, Players & Organizers" />
        <meta name="twitter:description" content="Launch your website, build your brand, and grow your revenue. The all-in-one platform for Coaches, Clubs, Creators, and Players." />
        <meta name="twitter:image" content="https://chessweb.site/SocialSharing.png" />

        {/* JSON-LD Organization + WebSite Schema */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://chessweb.site/#organization',
              name: 'ChessWeb',
              url: 'https://chessweb.site',
              logo: 'https://chessweb.site/SocialSharing.png',
              description: 'The all-in-one website platform for chess coaches, clubs, creators, and players.',
              sameAs: [
                'https://twitter.com/IndieChess',
                'https://www.youtube.com/@IndieChess',
                'https://www.instagram.com/indiechess/'
              ]
            },
            {
              '@type': 'WebSite',
              '@id': 'https://chessweb.site/#website',
              url: 'https://chessweb.site',
              name: 'ChessWeb',
              publisher: { '@id': 'https://chessweb.site/#organization' },
              description: 'Build your chess website in minutes. Coaching academies, online shops, tournament registration, blogs, and more.'
            }
          ]
        })}</script>
      </Helmet>

    <div className="min-h-screen bg-[#1a1a1a] text-white font-sans selection:bg-chess-green selection:text-white overflow-x-hidden">

      {/* Navigation Bar */}
      <nav className="fixed w-full z-50 bg-[#1a1a1a]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src="/Images/ChessWeb.site.png" alt="ChessWeb" className="h-8 w-8 rounded-md mr-3" />
              <span className="font-bold text-xl tracking-tight">ChessWeb<span className="text-chess-green">.site</span></span>
            </div>
           
            {/* Desktop: Features, Pricing, Learn & Login */}
            {!isMobile && (
              <div className="flex items-center gap-3">
                <a
                  href="#features"
                  className="text-gray-300 font-medium px-4 py-2 rounded-lg hover:bg-white/5 hover:text-white transition-colors"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="text-gray-300 font-medium px-4 py-2 rounded-lg hover:bg-white/5 hover:text-white transition-colors"
                >
                  Pricing
                </a>
                <a
                  href="#done-for-you"
                  className="text-gray-300 font-medium px-4 py-2 rounded-lg hover:bg-white/5 hover:text-white transition-colors"
                >
                  Done for You
                </a>
                <a
                  href="#demo-video"
                  className="text-gray-300 font-medium px-4 py-2 rounded-lg hover:bg-white/5 hover:text-white transition-colors"
                >
                  Learn
                </a>
                <button
                  onClick={currentUser ? () => navigate('/dashboard') : handleLogin}
                  className="text-chess-green font-bold px-4 py-2 border border-chess-green/40 rounded-lg hover:bg-chess-green hover:text-[#1a1a1a] transition-colors"
                >
                  {currentUser ? 'Dashboard' : 'Login'}
                </button>
              </div>
            )}

            {/* Mobile: Hamburger */}
            {isMobile && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2"
              >
                <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
              </button>
            )}
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobile && mobileMenuOpen && (
            <div className="border-t border-white/10 pb-4">
              <a
                href="#features"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center text-gray-300 font-medium px-4 py-3 hover:bg-white/5 hover:text-white transition-colors"
              >
                Features
              </a>
              <a
                href="#pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center text-gray-300 font-medium px-4 py-3 hover:bg-white/5 hover:text-white transition-colors"
              >
                Pricing
              </a>
              <a
                href="#done-for-you"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center text-gray-300 font-medium px-4 py-3 hover:bg-white/5 hover:text-white transition-colors"
              >
                Done for You
              </a>
              <a
                href="#demo-video"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center text-gray-300 font-medium px-4 py-3 hover:bg-white/5 hover:text-white transition-colors"
              >
                Learn
              </a>
              <div className="px-4 pt-3">
                <button
                  onClick={() => { setMobileMenuOpen(false); currentUser ? navigate('/dashboard') : handleLogin(); }}
                  className="w-full text-chess-green font-bold px-4 py-2 border border-chess-green/40 rounded-lg hover:bg-chess-green hover:text-[#1a1a1a] transition-colors"
                >
                  {currentUser ? 'Dashboard' : 'Login'}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section with Scroll Jacking */}
      <div
        ref={heroRef}
        className="relative min-h-screen bg-[#1a1a1a]"
        style={{ touchAction: isMobile ? 'pan-y' : 'auto' }}
      >
        <div className="min-h-screen flex items-center overflow-hidden pt-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-chess-green/20 via-[#1a1a1a] to-[#1a1a1a]"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              
              {/* Left Column: Hero Visual/Preview */}
              <div className="relative perspective-1000 order-last lg:order-first pb-20 lg:pb-0">
                <div className="relative flex flex-col items-center">
                  {/* Angled Container */}
                  <div className="relative transform transition-all duration-700 hover:rotate-0 md:rotate-y-6 md:hover:scale-105">

                    {/* Laptop Frame */}
                    <div
                      ref={laptopRef}
                      onClick={() => window.open('https://www.gmsindarov.com', '_blank', 'noopener,noreferrer')}
                      className="relative mx-auto cursor-pointer group"
                    >
                      {/* Screen */}
                      <div className="relative w-[320px] sm:w-[420px] md:w-[480px] bg-[#0c0c0c] rounded-t-xl border-[6px] border-[#5e5e5e] border-b-0 shadow-2xl overflow-hidden ring-1 ring-white/20 group-hover:ring-chess-green/50 transition-all">
                        {/* Screen bezel top (camera notch) */}
                        <div className="h-3 bg-[#6a6a6a] flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#4a4a4a]"></div>
                        </div>
                        {/* Screen content area */}
                        <div className="laptop-screen relative w-full aspect-[16/10] overflow-hidden bg-black">
                          <img
                            src="/Images/SindarovHero.png"
                            alt="GM Javokhir Sindarov — Built on ChessWeb.site"
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        {/* Reflection/Glass Effect */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none rounded-t-lg"></div>
                      </div>

                      {/* Laptop Base / Hinge */}
                      <div className="relative w-[320px] sm:w-[420px] md:w-[480px] mx-auto">
                        {/* Hinge strip */}
                        <div className="h-[6px] bg-gradient-to-b from-[#6a6a6a] to-[#505050] rounded-b-sm"></div>
                        {/* Base */}
                        <div
                          className="h-3 rounded-b-xl mx-auto"
                          style={{
                            width: '110%',
                            marginLeft: '-5%',
                            background: 'linear-gradient(to bottom, #5e5e5e, #444)',
                            borderBottomLeftRadius: '1rem',
                            borderBottomRightRadius: '1rem',
                          }}
                        >
                          {/* Trackpad notch */}
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[3px] bg-[#4a4a4a] rounded-t-sm"></div>
                        </div>
                      </div>
                    </div>

                    {/* Decorative glow behind */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-chess-green/20 blur-[80px] -z-10 rounded-full"></div>
                  </div>

                  {/* Caption */}
                  {/* Badge (Relocated) */}
                  <div className="mt-4 px-4 w-full flex justify-center">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm animate-fade-in-up">
                      <i className="fas fa-trophy text-yellow-500 mr-2 flex-shrink-0"></i>
                      <span className="text-xs sm:text-sm font-medium text-gray-300 text-center">Trusted by the World Champion Challenger</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Content */}
              <div className="text-center lg:text-left">

                <div className="flex justify-center lg:justify-start pt-20 md:pt-12 mb-6">
                  <i className="fas fa-chess-board text-5xl md:text-7xl text-chess-green fa-spin" style={{ animationDuration: '8s' }}></i>
                </div>

                <h1 className="text-4xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
                  The Operating System{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffd700] to-chess-cream">
                    for Chess.
                  </span>
                </h1>

                <p className="mt-4 text-xl text-gray-400 mb-10 max-w-2xl mx-auto lg:mx-0">
                  Websites, academies, libraries, and shops. Everything your chess business needs in one place.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8">
                <button
                    onClick={handleGetStarted}
                    className="w-full sm:w-auto px-8 py-4 bg-chess-green text-white rounded-xl font-bold text-lg hover:bg-opacity-90 transition-all transform hover:scale-105 shadow-xl shadow-chess-green/20 flex items-center justify-center"
                  >
                    {currentUser ? 'Continue Building' : 'Build Your Chess Website'}
                    <i className="fas fa-arrow-right ml-2"></i>
                  </button>
                  {!currentUser && (
                  <a
                    href="#features"
                    className="w-full sm:w-auto px-8 py-4 bg-white/5 text-white border border-white/10 rounded-xl font-bold text-lg hover:bg-white/10 transition-all flex items-center justify-center backdrop-blur-sm"
                  >
                    <i className="fas fa-chess-board mr-2 text-chess-cream"></i>
                    See Features
                  </a>
                  )}

                </div>

                <p className="text-sm text-gray-400 mt-4">
                  <i className="fas fa-bolt text-yellow-500 mr-1"></i>
                  14-day free trial. Cancel anytime.
                </p>
                
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Integrations Showcase - Horizontal Scroll */}
      <section className="py-6 md:py-10 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-full mx-auto px-4 text-center">
          <p className="text-sm font-semibold  uppercase tracking-wider mb-8" style={{ color: '#ffd700' }}>Powering the next generation of chess</p>

          {/* Horizontal Scroll Container */}
          <div className="relative">
            <div className="w-full overflow-hidden">
              <div className="w-full overflow-x-auto hide-scrollbar" ref={scrollContainerRef} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="flex flex-nowrap items-center gap-4 sm:gap-5 px-1" style={{ maxWidth: '100%' }}>
                  {loopedItems.map((item) => {
                    if (item.type === 'tag') {
                      const commonClasses = 'h-14 sm:h-16 px-5 sm:px-6 flex items-center justify-center flex-shrink-0 rounded-2xl shadow-lg font-semibold text-xs sm:text-sm uppercase tracking-wide';
                      if (item.href) {
                        const style = { backgroundColor: item.bg, color: item.textColor || '#0f0f0f', opacity: 0.6 };
                        return (
                          <a
                            key={item.loopKey}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={commonClasses}
                            style={style}
                          >
                            {item.label}
                          </a>
                        );
                      }
                      const style = { backgroundColor: item.bg, color: item.textColor || '#f8fafc', opacity: 0.6 };
                      return (
                        <div
                          key={item.loopKey}
                          className={commonClasses}
                          style={style}
                        >
                          {item.label}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={item.loopKey}
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden shadow-lg"
                        style={(() => {
                          if (item.type === 'icon') {
                            if (item.gradient) {
                              return { background: item.gradient, opacity: 0.6 };
                            }
                            return { backgroundColor: item.bg, opacity: 0.6 };
                          }
                          return { backgroundColor: item.bg || '#1B1B1B', opacity: 0.6 };
                        })()}
                      >
                        {item.type === 'image' ? (
                          <img
                            src={item.src}
                            alt={item.alt}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <>
                            <i
                              className={`${item.iconClass} text-2xl sm:text-3xl`}
                              style={{ color: item.iconColor || '#FFFFFF' }}
                              aria-hidden="true"
                            ></i>
                            <span className="sr-only">{item.label}</span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section id="features" className="py-12 md:py-24 relative" style={{ scrollMarginTop: '1.5rem' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Everything you need, nothing you don't.</h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg">Four products that work together to help you build a brand, monetize, and grow in chess.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {/* Website Hosting */}
            <div className="group relative rounded-2xl p-[1px] bg-gradient-to-b from-chess-green/30 to-transparent hover:from-chess-green/50 transition-all duration-300">
              <div className="rounded-2xl bg-[#0a0a0a] p-6 sm:p-8 flex flex-col h-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-chess-green/30 to-chess-green/10 flex items-center justify-center flex-shrink-0 shadow-lg shadow-chess-green/10">
                    <i className="fas fa-globe text-chess-green text-xl"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Website Hosting</h3>
                </div>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  Your own chess website in minutes. Drag-and-drop editor, custom domain, no coding required.
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'fa-paint-brush', text: '20+ block types, fully customizable' },
                    { icon: 'fa-link', text: 'Branded URL or your own .com domain' },
                    { icon: 'fa-pen-fancy', text: 'Blog, social links & contact forms built in' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-md bg-chess-green/10 flex items-center justify-center flex-shrink-0">
                        <i className={`fas ${item.icon} text-chess-green text-xs`}></i>
                      </div>
                      <span className="text-sm text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Library */}
            <div className="group relative rounded-2xl p-[1px] bg-gradient-to-b from-chess-green/30 to-transparent hover:from-chess-green/50 transition-all duration-300">
              <div className="rounded-2xl bg-[#0a0a0a] p-6 sm:p-8 flex flex-col h-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-chess-green/30 to-chess-green/10 flex items-center justify-center flex-shrink-0 shadow-lg shadow-chess-green/10">
                    <i className="fas fa-book-open text-chess-green text-xl"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Content Library</h3>
                </div>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  Deliver real chess content to your students. Interactive boards, puzzles, videos and more — perfect for courses and homework.
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'fa-chess-board', text: 'Interactive PGN studies & Lichess embeds' },
                    { icon: 'fa-puzzle-piece', text: 'Custom puzzles with hints & solutions' },
                    { icon: 'fa-clipboard-check', text: 'Assign homework or sell full courses' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-md bg-chess-green/10 flex items-center justify-center flex-shrink-0">
                        <i className={`fas ${item.icon} text-chess-green text-xs`}></i>
                      </div>
                      <span className="text-sm text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Students & Scheduling */}
            <div className="group relative rounded-2xl p-[1px] bg-gradient-to-b from-chess-green/30 to-transparent hover:from-chess-green/50 transition-all duration-300">
              <div className="rounded-2xl bg-[#0a0a0a] p-6 sm:p-8 flex flex-col h-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-chess-green/30 to-chess-green/10 flex items-center justify-center flex-shrink-0 shadow-lg shadow-chess-green/10">
                    <i className="fas fa-calendar-check text-chess-green text-xl"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Academy Management</h3>
                </div>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  Students book from your site and join via built-in video. You see everything in one dashboard.
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'fa-users', text: 'Group classes & 1-on-1 with built-in video' },
                    { icon: 'fa-user-check', text: 'Manage scheduling and students' },
                    { icon: 'fa-chart-line', text: 'Revenue & traffic analytics' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-md bg-chess-green/10 flex items-center justify-center flex-shrink-0">
                        <i className={`fas ${item.icon} text-chess-green text-xs`}></i>
                      </div>
                      <span className="text-sm text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Online Shop */}
            <div className="group relative rounded-2xl p-[1px] bg-gradient-to-b from-chess-green/30 to-transparent hover:from-chess-green/50 transition-all duration-300">
              <div className="rounded-2xl bg-[#0a0a0a] p-6 sm:p-8 flex flex-col h-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-chess-green/30 to-chess-green/10 flex items-center justify-center flex-shrink-0 shadow-lg shadow-chess-green/10">
                    <i className="fas fa-shopping-bag text-chess-green text-xl"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Online Shop</h3>
                </div>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  Sell products, process payments, and monetize your content directly from your website via Stripe.
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'fa-box-open', text: 'Digital products, courses & downloads' },
                    { icon: 'fa-lock', text: 'Gate access to your library or academy' },
                    { icon: 'fa-credit-card', text: 'Stripe Connect, payments straight to you' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-md bg-chess-green/10 flex items-center justify-center flex-shrink-0">
                        <i className={`fas ${item.icon} text-chess-green text-xs`}></i>
                      </div>
                      <span className="text-sm text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tagline below cards */}
          <p className="text-center  mt-16 text-lg font-medium" style={{ color: '#749555' }}>
            Easily toggle on/off features. Build only what you choose. Charge whatever you want.
          </p>
        </div>
      </section>

      <AcademyShowcase onGetStarted={handleGetStarted} />

      {/* How It Works */}
      <section id="launch-section" className="py-12 md:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-bold mb-12 text-center">A Premium Website, Ready in Minutes.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { step: "01", title: "Create your account", desc: "Sign up and get instant access to your dashboard." },
              { step: "02", title: "Configure your business", desc: "Populate your website, academy, shop, content library, and coaching schedule." },
              { step: "03", title: "Launch today", desc: "No developers, no waiting. Publish your chess website to the world." }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-chess-green/30 font-mono mb-3">{item.step}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="relative" id="demo-video" style={{ scrollMarginTop: '8rem' }}>
            <div className="absolute -inset-4 bg-chess-green/20 rounded-3xl blur-xl"></div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
              <iframe
                className="w-full aspect-video"
                src="https://www.youtube.com/embed/BNFo0UrWB18"
                title="ChessWeb.site Demo"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="text-center mt-8">
            <a
              href="/guide"
              className="inline-flex items-center gap-2 px-6 py-3 bg-chess-green/20 hover:bg-chess-green/30 text-chess-green border border-chess-green/30 rounded-lg font-semibold transition-colors"
            >
              View Full Guide
            </a>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <div id="pricing" style={{ scrollMarginTop: '7rem' }}>
        <PricingComparison />
      </div>

      {/* FAQ */}
      {/* <section className="py-24">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              { q: "Do I need technical skills?", a: "Not at all. If you can play chess, you can build your site. Our drag-and-drop editor makes it simple." },
              { q: "Can I use my own domain name?", a: "Yes! On any premium plan, you can connect a custom domain (like coachsmith.com) directly to your profile." },
              { q: "How do I get paid?", a: "We integrate with Stripe. Payments go directly to your bank account. We don't hold your money." },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-bold mb-2 text-white">{item.q}</h3>
                <p className="text-gray-400">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className="py-12 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-chess-green/10"></div>
        <div className="max-w-5xl mx-auto px-2 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold mb-8">Your chess business starts here.</h2>
          <p className="text-xl text-gray-300 mb-10">Build your coaching academy, online shop, and professional website — all through ChessWeb.site.</p>
          <button 
            onClick={handleGetStarted}
            className="px-10 py-5 bg-chess-green text-white rounded-xl font-bold text-xl hover:bg-opacity-90 transition-all transform hover:scale-105 shadow-2xl shadow-chess-green/30"
          >
            Launch My Website Now
            <i className="fas fa-arrow-right ml-2"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center mb-4">
                <img src="/Images/ChessWeb.site.png" alt="ChessWeb.site" className="h-8 w-8 rounded-md mr-3" />
                <span className="text-xl font-bold text-white">ChessWeb<span className="text-chess-green">.site</span></span>
              </div>
              <p className="text-gray-500 text-sm mb-4">
                The platform for chess coaches and professionals to build, launch, and monetize.
              </p>
              <a
                href="mailto:info@chessweb.site"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                info@chessweb.site
              </a>
            </div>

            {/* Product Column */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">Dashboard</Link></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#done-for-you" className="text-gray-400 hover:text-white transition-colors">Done for You</a></li>
                <li><a href="/guide" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Learn</a></li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="https://indiechess.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Sample Site</a></li>
                <li><Link to="/coach" className="text-gray-400 hover:text-white transition-colors">I'm a Student</Link></li>
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">See Features</a></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">
              &copy; {new Date().getFullYear()} ChessWeb.site. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://twitter.com/ChessWebSite" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                <i className="fab fa-twitter"></i>
              </a>
              <a href="https://youtube.com/@IndieChess" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                <i className="fab fa-youtube"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
          className="fixed bottom-20 right-8 z-[60] w-12 h-12 rounded-full bg-chess-green text-white shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
        >
          <i className="fas fa-chevron-up"></i>
        </button>
      )}
    </div>
    </>
  );
};

export default CreatorProfiles;
